import { Component } from '@theme/component';

/**
 * @typedef {Object} LayeredSlideshowRefs
 * @property {HTMLElement} container
 * @property {HTMLElement} track
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} [prevBtn]
 * @property {HTMLElement} [nextBtn]
 * @property {HTMLElement[]} [dots]
 */

/** @extends {Component<LayeredSlideshowRefs>} */
export class LayeredSlideshowComponent extends Component {
  requiredRefs = ['container'];
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { panels } = this.refs;
    if (!panels?.length) return;

    this.#active = 0;
    this.#setupEventListeners();
    this.#updateSlide();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { prevBtn, nextBtn, dots, track } = this.refs;

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.previous(), opts);
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.next(), opts);
    }

    if (dots) {
      for (const [i, dot] of dots.entries()) {
        dot.addEventListener('click', () => this.#activate(i), opts);
      }
    }

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    // Touch swipe support
    if (track) {
      let touchStartX = 0;
      track.addEventListener(
        'touchstart',
        (e) => {
          touchStartX = e.touches[0].clientX;
        },
        { ...opts, passive: true }
      );
      track.addEventListener('touchend', (e) => {
        const diff = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
          diff > 0 ? this.next() : this.previous();
        }
      }, opts);
    }
  }

  /** @param {KeyboardEvent} e */
  #handleKeydown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.previous();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    }
  }

  next() {
    const { panels } = this.refs;
    if (!panels) return;
    const nextIndex = (this.#active + 1) % panels.length;
    this.#activate(nextIndex);
  }

  previous() {
    const { panels } = this.refs;
    if (!panels) return;
    const prevIndex = (this.#active - 1 + panels.length) % panels.length;
    this.#activate(prevIndex);
  }

  /**
   * Public method to select a slide by index (used by theme editor)
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  select(index, { instant = false } = {}) {
    this.#activate(index, instant);
  }

  /**
   * @param {number} index
   * @param {boolean} [instant]
   */
  #activate(index, instant = false) {
    const { container, panels } = this.refs;
    if (!panels || index === this.#active || index < 0 || index >= panels.length) return;

    if (instant) {
      container.setAttribute('data-instant-transitions', '');
    }

    this.#active = index;
    this.#updateSlide();

    if (instant) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.removeAttribute('data-instant-transitions');
        });
      });
    }
  }

  #updateSlide() {
    const { container, panels, dots } = this.refs;

    // Slide the track
    container.style.setProperty('--active-index', String(this.#active));

    // Update panel states
    for (const [i, panel] of panels?.entries() ?? []) {
      const isActive = i === this.#active;
      panel.toggleAttribute('inert', !isActive);
      panel.setAttribute('tabindex', isActive ? '0' : '-1');

      const video = panel.querySelector('video');
      if (video) {
        isActive ? video.play() : video.pause();
      }
    }

    // Update dot indicators
    for (const [i, dot] of dots?.entries() ?? []) {
      if (i === this.#active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }
}

customElements.define('layered-slideshow-component', LayeredSlideshowComponent);
